/**
 * useMapInitialization Hook
 * Handles map initialization and setup
 */

import MapboxDraw from '@mapbox/mapbox-gl-draw';
import mapboxgl from 'mapbox-gl';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { drawStyle } from '@/config/drawStyles';
import { env } from '@/config/env';
import { INITIAL_CENTER, INITIAL_ZOOM, MAP_STYLE } from '@/config/mapConfig';
import { useMapbox } from '@/contexts/MapboxContext';
import { imagePreloader } from '@/services/map/ImagePreloader';
import { attachStyleReloadListener } from '@/services/map/mapReadiness';

export interface UseMapInitializationOptions {
  container: React.RefObject<HTMLDivElement>;
  onStyleLoad?: () => void | Promise<void>;
  onUserInteraction?: () => void;
}

/**
 * Hook for initializing the map
 * @param options - Initialization options
 */
export function useMapInitialization(options: UseMapInitializationOptions): void {
  const { map, draw, setMapReady } = useMapbox();
  const { container, onStyleLoad, onUserInteraction } = options;
  const initialized = useRef<boolean>(false);

  useEffect(() => {
    if (!container.current || initialized.current || map.current) return;

    // Set Mapbox access token before initializing map
    // Try multiple sources for the token
    const token =
      env.REACT_APP_MAPBOX_TOKEN ||
      import.meta.env.REACT_APP_MAPBOX_TOKEN ||
      import.meta.env.VITE_REACT_APP_MAPBOX_TOKEN;
    if (token && token.trim().length > 0) {
      mapboxgl.accessToken = token.trim();
      // Verify token format (should start with 'pk.' for public token)
      if (!token.startsWith('pk.') && !token.startsWith('sk.')) {
        console.warn('Mapbox token format may be incorrect. Expected format: pk.eyJ... or sk.eyJ...');
      }
    } else {
      console.error('REACT_APP_MAPBOX_TOKEN is not set. Map will not work without a valid token.');
      console.error('Please ensure REACT_APP_MAPBOX_TOKEN is set in your .env file.');
      console.error('Debug info:', {
        envToken: env.REACT_APP_MAPBOX_TOKEN,
        metaEnvToken: import.meta.env.REACT_APP_MAPBOX_TOKEN,
        viteToken: import.meta.env.VITE_REACT_APP_MAPBOX_TOKEN,
      });
      return;
    }

    // Ensure container is visible before initializing map
    if (container.current) {
      container.current.style.visibility = 'visible';
      container.current.style.opacity = '1';
    }

    // Initialize map
    map.current = new mapboxgl.Map({
      container: container.current,
      style: MAP_STYLE,
      center: INITIAL_CENTER as [number, number],
      zoom: INITIAL_ZOOM,
      attributionControl: false,
      antialias: true,
    });

    // Ensure map container stays visible
    const mapContainer = map.current.getContainer();
    if (mapContainer) {
      mapContainer.style.visibility = 'visible';
      mapContainer.style.opacity = '1';
    }

    // Add error handler to prevent map from crashing silently
    map.current.on('error', (e: { error?: Error & { message?: string }; type?: string; target?: unknown }) => {
      console.error('Mapbox error:', e);
      if (e.error) {
        console.error('Error details:', {
          message: e.error.message,
          type: e.type,
          target: e.target,
        });
        // Check if it's a token/auth error
        if (e.error.message?.includes('401') || e.error.message?.includes('Unauthorized')) {
          console.error('Mapbox token appears to be invalid or expired. Please check REACT_APP_MAPBOX_TOKEN.');
        }
      }
    });

    // Add navigation control
    const navigation = new mapboxgl.NavigationControl();
    map.current.addControl(navigation, 'bottom-right');

    // Initialize draw control
    draw.current = new MapboxDraw({
      displayControlsDefault: false,
      controls: {
        polygon: false,
      },
      styles: drawStyle,
    });
    map.current.addControl(draw.current);

    // Event listeners for user interaction
    if (onUserInteraction) {
      map.current.on('mousedown', onUserInteraction);
      map.current.on('dragstart', onUserInteraction);
      map.current.on('pitchstart', onUserInteraction);
      map.current.on('rotatestart', onUserInteraction);
      map.current.on('zoomstart', onUserInteraction);
    }

    // Attach style reload listener for layer status reset
    attachStyleReloadListener(map.current);

    // Handle style load
    map.current.on('style.load', () => {
      const layers = map.current.getStyle().layers;
      const labelLayerId = layers.find((layer) => layer.type === 'symbol' && 'text-field' in (layer.layout ?? {}))?.id;

      // Add 3D buildings layer only if it doesn't exist
      if (labelLayerId && !map.current.getLayer('add-3d-buildings')) {
        try {
          map.current.addLayer(
            {
              id: 'add-3d-buildings',
              source: 'composite',
              'source-layer': 'building',
              filter: ['==', 'extrude', 'true'],
              type: 'fill-extrusion',
              minzoom: 15,
              paint: {
                'fill-extrusion-color': '#aaa',
                'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], 15, 0, 15.05, ['get', 'height']],
                'fill-extrusion-base': ['interpolate', ['linear'], ['zoom'], 15, 0, 15.05, ['get', 'min_height']],
                'fill-extrusion-opacity': 0.8,
              },
            },
            labelLayerId,
          );
        } catch (error) {
          // Layer might already exist, ignore error
          console.warn('Could not add 3D buildings layer:', error);
        }
      }

      setMapReady(true);

      // Preload all custom icons once the style is ready
      if (map.current) {
        void imagePreloader.preloadAllIcons(map.current);
      }

      // Wait a bit to ensure style is fully ready before initializing layers
      setTimeout(() => {
        if (onStyleLoad) {
          onStyleLoad();
        }
      }, 100);
    });

    initialized.current = true;

    // Cleanup - only remove map on actual unmount, not on dependency changes
    return () => {
      // Check if container still exists in DOM - if not, it's a real unmount
      if (container.current?.parentNode) {
        // Container still exists, this is just a dependency change, don't cleanup
        return;
      }

      // Container removed from DOM, this is a real unmount
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
      initialized.current = false;
    };
  }, [container, map, draw, setMapReady, onStyleLoad, onUserInteraction]);
}
